// SIWS CUSTOM (2026-09-15): Cornwell Indy Funny Car Hat picker.
//
// Single-variant hat, NO minimum and NO increments (Michelle, 2026-09-11 email:
// "we aren't doing a minimum or designated increments"). That is the one structural
// difference from the Duck Camo hats picker this is modelled on — there is no swatch
// grid, no per-color snap unit, and no aggregate floor gating the ATC button.
//
// Tier ladder is per-each dollars, same shape as Duck Camo:
//   1-11 = $35 ea · 12+ = $33 ea · 24+ = $30 ea
//
// FREE SHIPPING rides the 24+ boundary. It is NOT enforced by anything in Shopify —
// it is a manual invoicing rule (the girls zero the freight line on 24+ orders). The
// callout therefore only ever states it as a CONDITION tied to a quantity, never as an
// unconditional promise. This is deliberate: the 2026-09 free-shipping incident was
// caused by a screen saying "Free" on an order that was then billed for shipping.
//
// Cart-aware: reads /cart.js so a dealer who already has 20 hats in the basket sees the
// 24+ tier when they add 4 more, rather than being told they are at the $35 tier.

(function () {
  'use strict';

  // Sorted DESCENDING by min — _getCurrentTier iterates top-down and returns first match.
  // PDP-side preview only; the actual discount applies via ONE Regios record
  // (Eligible line items), same pattern as the Duck Camo 5-Panel hats.
  const SIWS_INDY_TIERS = [
    { min: 24, perEach: 30.00, savings: 5.00, freeShip: true, max: true },
    { min: 12, perEach: 33.00, savings: 2.00, freeShip: false },
    { min: 0,  perEach: 35.00, savings: 0,    freeShip: false },
  ];

  const SIWS_INDY_BASE_EACH  = 35.00;
  const SIWS_INDY_FREE_SHIP  = 24;    // qty at which shipping is included
  const SIWS_INDY_MAX_TIER   = 24;    // top of ladder — progress bar denominator

  // Drop trailing .00 on whole dollars; comma-separate >= 1000. $35 (not $35.00).
  function siwsIndyFmt(amount) {
    const opts = amount % 1 === 0
      ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return '$' + amount.toLocaleString('en-US', opts);
  }

  function siwsIndyEsc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  class SIWSIndyHatPicker {
    constructor(el) {
      this.el          = el;
      this.variantId   = el.getAttribute('data-variant-id');
      this.productTitle= el.getAttribute('data-product-title') || 'Cornwell Indy Funny Car Hat';
      this.pickerQty   = 0;   // about to add
      this.cartQty     = 0;   // already in cart (cross-page-load tier awareness)
      this.totalQty    = 0;   // pickerQty + cartQty — drives tier math
      this._init();
    }

    _init() {
      this._qtyInput  = this.el.querySelector('#siws-indy-hat-qty-input');
      this._minusBtn  = this.el.querySelector('#siws-indy-hat-qty-minus');
      this._plusBtn   = this.el.querySelector('#siws-indy-hat-qty-plus');
      this._summaryEl = this.el.querySelector('#siws-indy-hat-summary');
      this._calloutEl = this.el.querySelector('#siws-indy-hat-tier-callout');
      this._atcBtn    = this.el.querySelector('#siws-indy-hat-atc-btn');
      this._msgEl     = this.el.querySelector('#siws-indy-hat-message');

      this._bindQty();
      this._bindAtc();
      this._update();

      this._fetchCartQty();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => this._fetchCartQty());
      }
    }

    // ── Cart awareness ───────────────────────────────────────
    // Matched on variant id, not on title text. Title matching is what made the cart
    // guard miss GS002 add-ons (RCA #1650) — an id either matches or it does not.
    _fetchCartQty() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : { items: [] }))
        .then((cart) => {
          this.cartQty = (cart.items || [])
            .filter((i) => String(i.variant_id) === String(this.variantId))
            .reduce((s, i) => s + i.quantity, 0);
          this._update();
        })
        .catch(() => { /* graceful degrade — keep last known cartQty */ });
    }

    // ── Quantity ─────────────────────────────────────────────
    _bindQty() {
      if (this._minusBtn) {
        this._minusBtn.addEventListener('click', () => {
          this._setQty(this.pickerQty - 1);
        });
      }
      if (this._plusBtn) {
        this._plusBtn.addEventListener('click', () => {
          this._setQty(this.pickerQty + 1);
        });
      }
      if (this._qtyInput) {
        this._qtyInput.addEventListener('input', () => {
          const raw = parseInt(this._qtyInput.value, 10);
          this.pickerQty = isNaN(raw) || raw < 0 ? 0 : raw;
          this._update();
        });
        this._qtyInput.addEventListener('blur', () => {
          this._setQty(this.pickerQty);
        });
      }
    }

    _setQty(n) {
      this.pickerQty = n < 0 ? 0 : n;
      if (this._qtyInput) {
        this._qtyInput.value = this.pickerQty === 0 ? '' : String(this.pickerQty);
      }
      this._update();
    }

    // ── Tier math ────────────────────────────────────────────
    _getCurrentTier() {
      for (let i = 0; i < SIWS_INDY_TIERS.length; i++) {
        if (this.totalQty >= SIWS_INDY_TIERS[i].min) return SIWS_INDY_TIERS[i];
      }
      return SIWS_INDY_TIERS[SIWS_INDY_TIERS.length - 1];
    }

    _getNextTier() {
      const upcoming = SIWS_INDY_TIERS
        .filter((t) => t.min > this.totalQty)
        .sort((a, b) => a.min - b.min);
      return upcoming.length ? upcoming[0] : null;
    }

    // ── Render ───────────────────────────────────────────────
    _update() {
      this.totalQty = this.pickerQty + this.cartQty;
      this._renderSummary();
      this._renderCallout();
      this._renderAtc();
    }

    // "Adding to cart" box — Michelle asked for this explicitly above the ATC button.
    // Priced at the tier the WHOLE order lands in, so the number here matches the invoice.
    _renderSummary() {
      if (!this._summaryEl) return;
      if (this.pickerQty <= 0) {
        this._summaryEl.hidden = true;
        this._summaryEl.innerHTML = '';
        return;
      }

      const tier    = this._getCurrentTier();
      const each    = tier.perEach;
      const lineTot = each * this.pickerQty;

      let cartNote = '';
      if (this.cartQty > 0) {
        cartNote =
          '<tr><td colspan="2" class="siws-indy-hat__summary-note">' +
            'Includes ' + this.cartQty + ' already in your cart when pricing the tier.' +
          '</td></tr>';
      }

      this._summaryEl.innerHTML =
        '<p class="siws-indy-hat__summary-title">Adding To Cart</p>' +
        '<table class="siws-indy-hat__summary-table"><tbody>' +
          '<tr>' +
            '<td>' + siwsIndyEsc(this.productTitle) + '</td>' +
            '<td class="siws-indy-hat__summary-qty-cell">' +
              this.pickerQty + ' &times; ' + siwsIndyFmt(each) +
            '</td>' +
          '</tr>' +
          cartNote +
          '<tr class="siws-indy-hat__summary-total-row">' +
            '<td>Total</td>' +
            '<td class="siws-indy-hat__summary-qty-cell">' + siwsIndyFmt(lineTot) + '</td>' +
          '</tr>' +
        '</tbody></table>';
      this._summaryEl.hidden = false;
    }

    _renderCallout() {
      if (!this._calloutEl) return;

      if (this.totalQty <= 0) {
        this._calloutEl.innerHTML =
          '<p class="siws-indy-hat__hint">' +
            'Order 12 or more for ' + siwsIndyFmt(33) + '/each &mdash; ' +
            '24 or more for ' + siwsIndyFmt(30) + '/each plus free shipping.' +
          '</p>';
        return;
      }

      const current    = this._getCurrentTier();
      const next       = this._getNextTier();
      const savingsNow = current.savings * this.totalQty;
      const progressPct= Math.min((this.totalQty / SIWS_INDY_MAX_TIER) * 100, 100);

      // Top of ladder — price AND shipping both unlocked.
      if (!next || current.max) {
        this._calloutEl.innerHTML =
          '<div class="siws-indy-hat__callout siws-indy-hat__callout--max">' +
            '<span class="siws-indy-hat__callout-icon">&#127881;</span>' +
            '<div class="siws-indy-hat__callout-text">' +
              '<p class="siws-indy-hat__callout-primary">Best Price Unlocked: ' +
                siwsIndyFmt(current.perEach) + '/Each</p>' +
              '<p class="siws-indy-hat__callout-secondary">Saving ' +
                siwsIndyFmt(savingsNow) + ' On This Order</p>' +
              '<p class="siws-indy-hat__callout-ship siws-indy-hat__callout-ship--won">' +
                '&#128666; Shipping Included &mdash; 24+ Hats</p>' +
            '</div>' +
          '</div>';
        return;
      }

      const remaining = next.min - this.totalQty;

      const primaryText = current.savings > 0
        ? siwsIndyFmt(current.perEach) + '/Each Unlocked &mdash; ' + this.totalQty + ' Hats &mdash; Saving ' +
          siwsIndyFmt(savingsNow) + ' On This Order'
        : siwsIndyFmt(current.perEach) + '/Each &mdash; ' + this.totalQty + ' Hat' +
          (this.totalQty === 1 ? '' : 's');

      const secondaryText =
        'Add ' + remaining + ' More For ' + siwsIndyFmt(next.perEach) + '/Each';

      // Free shipping is the chase only while it is still ahead of them. Shown in the
      // brand red (#dc2626) David asked for, and always qualified by the 24+ condition.
      let shipLine = '';
      if (this.totalQty < SIWS_INDY_FREE_SHIP) {
        const shipRemaining = SIWS_INDY_FREE_SHIP - this.totalQty;
        shipLine =
          '<p class="siws-indy-hat__callout-ship">' +
            '&#128666; Add ' + shipRemaining + ' More For FREE SHIPPING' +
          '</p>';
      }

      this._calloutEl.innerHTML =
        '<div class="siws-indy-hat__callout">' +
          '<span class="siws-indy-hat__callout-icon">&uarr;</span>' +
          '<div class="siws-indy-hat__callout-text">' +
            '<p class="siws-indy-hat__callout-primary">' + primaryText + '</p>' +
            '<p class="siws-indy-hat__callout-secondary">' + secondaryText + '</p>' +
            shipLine +
            '<div class="siws-indy-hat__callout-progress">' +
              '<div class="siws-indy-hat__callout-progress-bar" ' +
                   'style="width:' + progressPct.toFixed(1) + '%;"></div>' +
            '</div>' +
            '<p class="siws-indy-hat__callout-progress-label">' +
              this.totalQty + ' / ' + SIWS_INDY_MAX_TIER + '</p>' +
          '</div>' +
        '</div>';
    }

    // No minimum — the button is live as soon as there is 1 in the box.
    _renderAtc() {
      if (!this._atcBtn) return;
      if (this.pickerQty <= 0) {
        this._atcBtn.disabled    = true;
        this._atcBtn.textContent = 'Choose a quantity';
      } else {
        this._atcBtn.disabled    = false;
        this._atcBtn.textContent = 'Add to cart';
      }
    }

    // ── Add to cart ──────────────────────────────────────────
    _bindAtc() {
      if (!this._atcBtn) return;
      this._atcBtn.addEventListener('click', () => {
        if (this.pickerQty <= 0) return;

        this._clearMessage();
        this._atcBtn.disabled    = true;
        this._atcBtn.textContent = 'Adding…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(this.variantId), quantity: this.pickerQty }],
          }),
        })
          .then((r) => r.json().then((body) => ({ ok: r.ok, body })))
          .then(({ ok, body }) => {
            if (!ok) {
              this._showMessage(body.description || body.message || 'Could not add to cart.');
              this._renderAtc();
              return;
            }
            this._setQty(0);
            this._fetchCartQty();
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'siws-indy-funny-car-hat',
                cartData: body,
              });
            }
            this._renderAtc();
          })
          .catch(() => {
            this._showMessage('Could not add to cart. Please try again.');
            this._renderAtc();
          });
      });
    }

    // ── Inline messages ──────────────────────────────────────
    _showMessage(msg) {
      if (!this._msgEl) return;
      this._msgEl.textContent   = msg;
      this._msgEl.style.display = 'block';
    }
    _clearMessage() {
      if (!this._msgEl) return;
      this._msgEl.style.display = 'none';
    }
  }

  function siwsIndyInit() {
    document.querySelectorAll('.siws-indy-hat__container').forEach((el) => {
      new SIWSIndyHatPicker(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', siwsIndyInit);
  } else {
    siwsIndyInit();
  }
})();
